import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Gavel from '@mui/icons-material/GavelOutlined';
import type { SxProps, Theme } from '@mui/material/styles';
import FormCard from './FormCard';
import FormField from './FormField';
import SelectInput from './SelectInput';
import {
  CONSIGNEE_TYPES,
  CURRENCIES,
  CUSTOMS_DISPLAY_DEFAULTS,
  CUSTOMS_UNITS,
  CustomsInformation,
  CustomsItemDraft,
  EXPORT_REASONS,
  INCOTERMS,
  isBlank,
  plural,
} from './labelForm';
import { addButtonSx, denseInputSx, inputSx, numericInputProps, rowWellSx } from './styles';

const twoColSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
  gap: '14px 12px',
};

/** '' -> NaN keeps the pre-redesign payload semantics for cleared numeric fields. */
const toNumber = (value: string) => parseFloat(value);
const fromNumber = (value: number | undefined) => (value === undefined || Number.isNaN(value) ? '' : value);

interface CustomsItemRowProps {
  item: CustomsItemDraft;
  index: number;
  removable: boolean;
  onChange: (key: string, patch: Partial<CustomsItemDraft>) => void;
  onRemove: (key: string) => void;
}

const CustomsItemRow = memo(function CustomsItemRow({ item, index, removable, onChange, onRemove }: CustomsItemRowProps) {
  const { key } = item;
  return (
    <Box sx={[rowWellSx, { p: 1.5 }] as SxProps<Theme>}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>Item {index + 1}</Typography>
        {removable && (
          <Tooltip title="Remove customs item">
            <IconButton size="small" aria-label={`Remove customs item ${index + 1}`} onClick={() => onRemove(key)} sx={{ ml: 'auto', color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(6, minmax(0, 1fr))' }, gap: '12px' }}>
        <FormField label="Commodity description" required sx={{ gridColumn: { sm: 'span 4' } }}>
          <OutlinedInput
            value={item.commodityDescription}
            placeholder="Min 3 characters"
            error={isBlank(item.commodityDescription)}
            onChange={(e) => onChange(key, { commodityDescription: e.target.value })}
            sx={denseInputSx}
          />
        </FormField>
        <FormField label="Country of manufacture" required sx={{ gridColumn: { sm: 'span 2' } }}>
          <OutlinedInput
            value={item.countryOfManufacture}
            placeholder="US, CN…"
            onChange={(e) => onChange(key, { countryOfManufacture: e.target.value.toUpperCase() })}
            inputProps={{ maxLength: 2 }}
            sx={denseInputSx}
          />
        </FormField>
        <FormField label="Quantity" required sx={{ gridColumn: { sm: 'span 2' } }}>
          <OutlinedInput
            type="number"
            value={fromNumber(item.quantity)}
            onChange={(e) => onChange(key, { quantity: parseInt(e.target.value, 10) })}
            inputProps={{ ...numericInputProps, min: 1 }}
            sx={denseInputSx}
          />
        </FormField>
        <FormField label="Customs value" required sx={{ gridColumn: { sm: 'span 2' } }}>
          <OutlinedInput
            type="number"
            value={fromNumber(item.customsValue)}
            onChange={(e) => onChange(key, { customsValue: toNumber(e.target.value) })}
            inputProps={{ ...numericInputProps, step: 0.01, min: 0 }}
            sx={denseInputSx}
          />
        </FormField>
        <FormField label="Unit" sx={{ gridColumn: { sm: 'span 2' } }}>
          <SelectInput
            dense
            label="Unit of measurement"
            value={item.quantityUnitOfMeasurement || 'piece'}
            options={CUSTOMS_UNITS}
            onChange={(quantityUnitOfMeasurement) => onChange(key, { quantityUnitOfMeasurement })}
          />
        </FormField>
        <FormField label="Product ID" optional sx={{ gridColumn: { sm: 'span 3' } }}>
          <OutlinedInput value={item.productId || ''} onChange={(e) => onChange(key, { productId: e.target.value })} sx={denseInputSx} />
        </FormField>
        <FormField label="Harmonized tariff number" optional sx={{ gridColumn: { sm: 'span 3' } }}>
          <OutlinedInput
            value={item.harmonizedTariffNumber || ''}
            placeholder="6–15 digit HS code"
            onChange={(e) => onChange(key, { harmonizedTariffNumber: e.target.value })}
            sx={denseInputSx}
          />
        </FormField>
      </Box>
    </Box>
  );
});

interface CustomsCardProps {
  customs: CustomsInformation | undefined;
  items: CustomsItemDraft[];
  onChange: (patch: Partial<CustomsInformation>) => void;
  onItemChange: (key: string, patch: Partial<CustomsItemDraft>) => void;
  onAddItem: () => void;
  onRemoveItem: (key: string) => void;
}

const CustomsCard: React.FC<CustomsCardProps> = ({ customs, items, onChange, onItemChange, onAddItem, onRemoveItem }) => {
  const info = customs ?? {};
  return (
    <FormCard icon={<Gavel />} title="Customs" summary={plural(items.length, 'item')}>
      <Box sx={twoColSx}>
        <FormField label="Total customs value" required>
          <OutlinedInput
            type="number"
            value={fromNumber(info.totalCustomsValue)}
            error={!(Number(info.totalCustomsValue) > 0)}
            onChange={(e) => onChange({ totalCustomsValue: toNumber(e.target.value) })}
            inputProps={{ ...numericInputProps, step: 0.01, min: 0 }}
            sx={inputSx}
          />
        </FormField>
        <FormField label="Currency">
          <SelectInput
            label="Currency"
            value={info.totalCustomsValueCurrency || CUSTOMS_DISPLAY_DEFAULTS.totalCustomsValueCurrency}
            options={CURRENCIES}
            onChange={(totalCustomsValueCurrency) => onChange({ totalCustomsValueCurrency })}
          />
        </FormField>
        <FormField label="Detailed description of items" required sx={{ gridColumn: '1 / -1' }}>
          <OutlinedInput
            multiline
            minRows={2}
            value={info.customsDescription || ''}
            error={isBlank(info.customsDescription)}
            onChange={(e) => onChange({ customsDescription: e.target.value })}
            sx={{ borderRadius: 2, fontSize: 14, bgcolor: 'background.paper' }}
          />
        </FormField>
        <FormField label="Reason for export">
          <SelectInput
            label="Reason for export"
            value={info.reasonForExport || CUSTOMS_DISPLAY_DEFAULTS.reasonForExport}
            options={EXPORT_REASONS}
            onChange={(reasonForExport) => onChange({ reasonForExport })}
          />
        </FormField>
        <FormField label="Ultimate consignee type">
          <SelectInput
            label="Ultimate consignee type"
            value={info.ultimateConsigneeType || CUSTOMS_DISPLAY_DEFAULTS.ultimateConsigneeType}
            options={CONSIGNEE_TYPES}
            onChange={(ultimateConsigneeType) => onChange({ ultimateConsigneeType })}
          />
        </FormField>
        <FormField label="Invoice date" optional>
          <OutlinedInput type="date" value={info.invoiceDate || ''} onChange={(e) => onChange({ invoiceDate: e.target.value })} sx={inputSx} />
        </FormField>
        <FormField label="Invoice number" optional>
          <OutlinedInput value={info.invoiceNumber || ''} onChange={(e) => onChange({ invoiceNumber: e.target.value })} sx={inputSx} />
        </FormField>
        <FormField label="AES internal transaction number" optional>
          <OutlinedInput
            value={info.aesInternalTransactionNumber || ''}
            placeholder="AESX20220714987654"
            onChange={(e) => onChange({ aesInternalTransactionNumber: e.target.value })}
            sx={inputSx}
          />
        </FormField>
        <FormField label="Incoterm">
          <SelectInput label="Incoterm" value={info.incoterm || CUSTOMS_DISPLAY_DEFAULTS.incoterm} options={INCOTERMS} onChange={(incoterm) => onChange({ incoterm })} />
        </FormField>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography component="h3" sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
          Customs items
        </Typography>
        {items.map((item, index) => (
          <CustomsItemRow key={item.key} item={item} index={index} removable={items.length > 1} onChange={onItemChange} onRemove={onRemoveItem} />
        ))}
        <Box>
          <Button variant="outlined" startIcon={<Add />} onClick={onAddItem} sx={addButtonSx}>
            Add customs item
          </Button>
        </Box>
      </Box>
    </FormCard>
  );
};

export default memo(CustomsCard);
